/* Hifi receiver audio player -------------------------------------------------
   Drives <hifi-player>: transport, scrubber, volume/mute, cross-page state
   persistence, and audio-reactive VU meters (with a simulated fallback when
   the analyser can't read the media — e.g. a CORS-tainted source). */

const HIFI_STORAGE_KEY = 'ultraground:hifi';
const HIFI_REST_ANGLE = -45; // needle at rest (deg)
const HIFI_FULL_ANGLE = 45; // needle at full scale (deg)
const HIFI_VOL_SWEEP = 270; // knob rotation range (deg)

class HifiPlayer extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.audio = this.querySelector('[data-hifi-audio]');
    this.playBtn = this.querySelector('[data-hifi-play]');
    this.muteBtn = this.querySelector('[data-hifi-mute]');
    this.scrubber = this.querySelector('[data-hifi-scrubber]');
    this.volume = this.querySelector('[data-hifi-volume]');
    this.knob = this.querySelector('[data-hifi-knob]');
    this.currentTimeEl = this.querySelector('[data-hifi-current]');
    this.totalTimeEl = this.querySelector('[data-hifi-total]');
    this.meters = Array.from(this.querySelectorAll('[data-hifi-meter]'));

    if (!this.audio) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.seeking = false;
    this.lastPersist = 0;

    this.state = this.readState();
    this.applyInitialState();
    this.bindEvents();

    this.classList.add('hifi--ready');
  }

  /* --- Persistence ------------------------------------------------------- */
  readState() {
    const fallback = {
      time: 0,
      playing: false,
      volume: Number(this.dataset.defaultVolume || 60),
      muted: false,
      collapsed: this.dataset.startCollapsed === 'true',
    };
    try {
      const stored = JSON.parse(localStorage.getItem(HIFI_STORAGE_KEY));
      return stored ? { ...fallback, ...stored } : fallback;
    } catch (e) {
      return fallback;
    }
  }

  persist(force) {
    const now = Date.now();
    if (!force && now - this.lastPersist < 2000) return;
    this.lastPersist = now;
    try {
      localStorage.setItem(
        HIFI_STORAGE_KEY,
        JSON.stringify({
          time: this.audio.currentTime || 0,
          playing: !this.audio.paused,
          volume: Math.round(this.audio.volume * 100),
          muted: this.audio.muted,
          collapsed: this.classList.contains('hifi--collapsed'),
        })
      );
    } catch (e) {
      /* storage unavailable (private mode quota) — ignore */
    }
  }

  /* --- Initial state ----------------------------------------------------- */
  applyInitialState() {
    const vol = Math.min(100, Math.max(0, this.state.volume));
    this.audio.volume = vol / 100;
    this.audio.muted = this.state.muted;
    this.volume.value = vol;
    this.updateVolumeUI(vol);
    this.classList.toggle('hifi--muted', this.state.muted);
    this.muteBtn.setAttribute('aria-pressed', String(this.state.muted));
    this.classList.toggle('hifi--collapsed', this.state.collapsed);

    const restoreTime = () => {
      if (this.state.time && Number.isFinite(this.audio.duration)) {
        try {
          this.audio.currentTime = Math.min(this.state.time, this.audio.duration - 0.25);
        } catch (e) {
          /* seeking before ready — ignore */
        }
      }
      this.updateTimes();
      // Attempt to resume playback if it was running; browsers may block this
      // until a user gesture, in which case we stay paused (not a bug).
      if (this.state.playing) this.attemptPlay();
    };

    if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) {
      restoreTime();
    } else {
      this.audio.addEventListener('loadedmetadata', restoreTime, { once: true });
    }
  }

  /* --- Event wiring ------------------------------------------------------ */
  bindEvents() {
    this.playBtn.addEventListener('click', () => this.togglePlay());
    this.muteBtn.addEventListener('click', () => this.toggleMute());

    this.querySelectorAll('[data-hifi-collapse]').forEach((el) =>
      el.addEventListener('click', () => this.setCollapsed(true))
    );
    this.querySelectorAll('[data-hifi-expand]').forEach((el) =>
      el.addEventListener('click', () => this.setCollapsed(false))
    );

    this.scrubber.addEventListener('input', () => {
      this.seeking = true;
      const pct = Number(this.scrubber.value) / 100;
      if (Number.isFinite(this.audio.duration)) {
        this.audio.currentTime = pct * this.audio.duration;
      }
      this.scrubber.style.setProperty('--hifi-progress', `${this.scrubber.value}%`);
      this.updateTimes();
    });
    this.scrubber.addEventListener('change', () => {
      this.seeking = false;
    });

    this.volume.addEventListener('input', () => {
      this.ensureAudio();
      const v = Number(this.volume.value);
      this.audio.volume = v / 100;
      if (v > 0 && this.audio.muted) this.toggleMute(false);
      this.updateVolumeUI(v);
      this.persist(true);
    });

    this.audio.addEventListener('timeupdate', () => {
      if (!this.seeking) this.updateTimes();
      this.persist(false);
    });
    this.audio.addEventListener('loadedmetadata', () => this.updateTimes());
    this.audio.addEventListener('play', () => this.onPlay());
    this.audio.addEventListener('pause', () => this.onPause());

    const flush = () => this.persist(true);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  /* --- Transport --------------------------------------------------------- */
  // Build/resume the Web Audio graph. Only call from a real user gesture:
  // creating the graph without one can route audio through a suspended
  // context and play silently on browsers that otherwise allow autoplay.
  ensureAudio() {
    this.setupAudioGraph();
    if (this.audioCtx && this.audioCtx.state === 'suspended') this.audioCtx.resume();
  }

  togglePlay() {
    this.ensureAudio();
    if (this.audio.paused) this.attemptPlay();
    else this.audio.pause();
  }

  attemptPlay() {
    const p = this.audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay blocked — remain paused; UI already reflects paused state.
      });
    }
  }

  onPlay() {
    this.classList.add('hifi--playing');
    this.playBtn.setAttribute('aria-label', 'Pause');
    this.playBtn.setAttribute('aria-pressed', 'true');
    this.startMeters();
    this.persist(true);
  }

  onPause() {
    this.classList.remove('hifi--playing');
    this.playBtn.setAttribute('aria-label', 'Play');
    this.playBtn.setAttribute('aria-pressed', 'false');
    this.persist(true);
  }

  toggleMute(force) {
    const next = typeof force === 'boolean' ? force : !this.audio.muted;
    this.audio.muted = next;
    this.classList.toggle('hifi--muted', next);
    this.muteBtn.setAttribute('aria-pressed', String(next));
    this.persist(true);
  }

  setCollapsed(collapsed) {
    this.classList.toggle('hifi--collapsed', collapsed);
    this.persist(true);
  }

  /* --- Display helpers --------------------------------------------------- */
  updateTimes() {
    const cur = this.audio.currentTime || 0;
    const dur = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
    this.currentTimeEl.textContent = this.formatTime(cur);
    this.totalTimeEl.textContent = this.formatTime(dur);
    const pct = dur ? (cur / dur) * 100 : 0;
    if (!this.seeking) {
      this.scrubber.value = pct;
      this.scrubber.style.setProperty('--hifi-progress', `${pct}%`);
    }
  }

  updateVolumeUI(v) {
    const angle = -HIFI_VOL_SWEEP / 2 + (v / 100) * HIFI_VOL_SWEEP;
    this.knob.style.setProperty('--vol-angle', `${angle}deg`);
  }

  formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  /* --- VU meters --------------------------------------------------------- */
  setupAudioGraph() {
    if (this.audioCtx || this.graphFailed) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) {
      this.graphFailed = true;
      return;
    }
    try {
      this.audioCtx = new Ctx();
      const source = this.audioCtx.createMediaElementSource(this.audio);
      const splitter = this.audioCtx.createChannelSplitter(2);
      source.connect(splitter);
      source.connect(this.audioCtx.destination);
      this.analysers = [0, 1].map((ch) => {
        const a = this.audioCtx.createAnalyser();
        a.fftSize = 256;
        a.smoothingTimeConstant = 0.7;
        splitter.connect(a, ch);
        return a;
      });
      this.buffers = this.analysers.map((a) => new Uint8Array(a.fftSize));
    } catch (e) {
      // createMediaElementSource can only run once / may be blocked — fall
      // back to a simulated meter so the needles still move.
      this.graphFailed = true;
    }
  }

  startMeters() {
    if (this.reduceMotion || this.metersRunning) return;
    this.metersRunning = true;
    this.flatFrames = 0;
    this.simulate = false;
    const tick = () => {
      if (this.audio.paused) {
        this.metersRunning = false;
        this.restMeters();
        return;
      }
      this.renderMeters();
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  renderMeters() {
    let levels;
    if (this.analysers && !this.simulate && !this.audio.muted) {
      levels = this.analysers.map((analyser, i) => {
        analyser.getByteTimeDomainData(this.buffers[i]);
        let sum = 0;
        for (let n = 0; n < this.buffers[i].length; n++) {
          const v = (this.buffers[i][n] - 128) / 128;
          sum += v * v;
        }
        return Math.min(1, Math.sqrt(sum / this.buffers[i].length) * 2.2);
      });
      // Detect a dead analyser (CORS-tainted media) and switch to simulation.
      if (levels[0] < 0.001 && (levels[1] === undefined || levels[1] < 0.001)) {
        if (++this.flatFrames > 40) {
          this.simulate = true;
          console.info('[hifi] VU analyser inactive — using simulated meters.');
        }
      } else {
        this.flatFrames = 0;
      }
    }

    if (!levels || this.simulate) levels = this.simulatedLevels();

    this.meters.forEach((meter, i) => {
      const level = this.audio.muted ? 0 : levels[i % levels.length];
      const angle = HIFI_REST_ANGLE + level * (HIFI_FULL_ANGLE - HIFI_REST_ANGLE);
      meter.style.setProperty('--vu-angle', `${angle}deg`);
    });
  }

  simulatedLevels() {
    // Smooth pseudo-random bounce keyed to time; no audio analysis needed.
    const t = (this.audio.currentTime || 0) * 1000 + performance.now();
    const wobble = (phase) =>
      0.45 + 0.35 * Math.abs(Math.sin(t / 280 + phase)) + 0.15 * Math.abs(Math.sin(t / 90 + phase));
    return [Math.min(1, wobble(0)), Math.min(1, wobble(1.7))];
  }

  restMeters() {
    this.meters.forEach((meter) => meter.style.setProperty('--vu-angle', `${HIFI_REST_ANGLE}deg`));
  }
}

if (!customElements.get('hifi-player')) {
  customElements.define('hifi-player', HifiPlayer);
}
